import { CalendarCheck, ClockCounterClockwise, CurrencyDollar, FileText, Globe, LinkSimple, ShieldCheck, Sparkle } from '@phosphor-icons/react'
import { type Language } from './LandingPage'

const content = {
  pt: {
    thesis: [
      { title: 'Para reservas com configuração clara', description: 'PinDay respeita o que o serviço vende: data, horário ou janela, sempre com hold real e política clara.' },
      { title: 'Cobrança única ou assinatura', description: 'One-off com sinal para reservas pontuais ou recorrente via Stripe para operações contínuas.' },
      { title: 'Pagamento, regras e calendário no mesmo fluxo', description: 'Sem depender de remendos entre proposta, contrato, checkout e bloqueio no calendário.' },
    ],
    featuresTitle: 'O que o produto entrega hoje',
    features: [
      { icon: CurrencyDollar, title: 'One-off com sinal', description: 'Cobrança única com sinal, saldo posterior e regras de cancelamento.' },
      { icon: LinkSimple, title: 'Recorrente via Stripe', description: 'Assinatura no checkout para Pro e Studio, sem sinal no fluxo atual.' },
      { icon: CalendarCheck, title: 'Soft hold e hard hold', description: 'Reserva temporária antes do pagamento e bloqueio firme após confirmação.' },
      { icon: Globe, title: 'Google Calendar + ICS fallback', description: 'Sincronize o bloqueio com Google Calendar e mantenha fallback via ICS.' },
      { icon: ShieldCheck, title: 'Reembolso e reagendamento por regra', description: 'Calcule reembolso, retenção e taxa de reagendamento com política definida.' },
      { icon: FileText, title: 'Termos com aceite registrado', description: 'Aceite com IP, timestamp e recibo auditável.' },
      { icon: ClockCounterClockwise, title: 'Link de saldo e lembretes', description: 'Envie saldo posterior e lembretes automáticos para pagamentos restantes.' },
      { icon: Sparkle, title: 'Fila de espera Pro+', description: 'Se uma data for liberada no PinDay, o próximo da fila recebe uma oferta exclusiva de 24h por e-mail.' },
    ],
  },
  en: {
    thesis: [
      { title: 'For bookings with clear configuration', description: 'PinDay respects what the service sells: date, time slot, or window, always with real holds and clear policy.' },
      { title: 'Single charge or subscription', description: 'One-off with deposit for date bookings or recurring Stripe subscriptions for ongoing operations.' },
      { title: 'Payment, rules, and calendar in the same flow', description: 'No patchwork between proposal, contract, checkout, and calendar blocking.' },
    ],
    featuresTitle: 'What the product delivers today',
    features: [
      { icon: CurrencyDollar, title: 'One-off with deposit', description: 'Single charge with deposit, later balance, and clear cancellation rules.' },
      { icon: LinkSimple, title: 'Recurring via Stripe', description: 'Subscription checkout for Pro and Studio, with no deposit in the current flow.' },
      { icon: CalendarCheck, title: 'Soft hold and hard hold', description: 'Temporary reservation before payment and firm calendar block after confirmation.' },
      { icon: Globe, title: 'Google Calendar + ICS fallback', description: 'Sync the block with Google Calendar and keep ICS as fallback.' },
      { icon: ShieldCheck, title: 'Refund and reschedule rules', description: 'Calculate refunds, retention, and reschedule fees from your policy.' },
      { icon: FileText, title: 'Terms with recorded acceptance', description: 'Acceptance with IP, timestamp, and auditable receipts.' },
      { icon: ClockCounterClockwise, title: 'Balance link and reminders', description: 'Send later balances with automatic reminders for the remaining amount.' },
      { icon: Sparkle, title: 'Pro+ waitlist', description: 'If a date becomes available in PinDay, the next person in line receives an exclusive 24-hour offer by email.' },
    ],
  },
  es: {
    thesis: [
      { title: 'Para reservas con configuración clara', description: 'PinDay respeta lo que vende el servicio: fecha, horario o ventana, siempre con hold real y reglas claras.' },
      { title: 'Cobro único o suscripción', description: 'One-off con seña para reservas puntuales o recurrente vía Stripe para operaciones continuas.' },
      { title: 'Pago, reglas y calendario en el mismo flujo', description: 'Sin remiendos entre propuesta, contrato, checkout y bloqueo en el calendario.' },
    ],
    featuresTitle: 'Lo que el producto entrega hoy',
    features: [
      { icon: CurrencyDollar, title: 'One-off con seña', description: 'Cobro único con seña, saldo posterior y reglas claras de cancelación.' },
      { icon: LinkSimple, title: 'Recurrente vía Stripe', description: 'Checkout de suscripción para Pro y Studio, sin seña en el flujo actual.' },
      { icon: CalendarCheck, title: 'Soft hold y hard hold', description: 'Reserva temporal antes del pago y bloqueo firme después de la confirmación.' },
      { icon: Globe, title: 'Google Calendar + fallback ICS', description: 'Sincroniza el bloqueo con Google Calendar y mantén ICS como respaldo.' },
      { icon: ShieldCheck, title: 'Reembolso y reagendamiento por regla', description: 'Calcula reembolso, retención y tasa de reprogramación desde tu política.' },
      { icon: FileText, title: 'Términos con aceptación registrada', description: 'Aceptación con IP, timestamp y recibos auditables.' },
      { icon: ClockCounterClockwise, title: 'Link de saldo y recordatorios', description: 'Envía saldo posterior y recordatorios automáticos para el monto pendiente.' },
      { icon: Sparkle, title: 'Lista de espera Pro+', description: 'Si una fecha se libera en PinDay, la siguiente persona de la fila recibe una oferta exclusiva de 24 horas por email.' },
    ],
  },
}

interface DifferentiatorsProps {
  language: Language
}

export function Differentiators({ language }: DifferentiatorsProps) {
  const t = content[language]

  return (
    <section className="px-6 py-20">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 lg:grid-cols-3">
          {t.thesis.map((item) => (
            <article key={item.title} className="rounded-[28px] border border-[#0b2a4a]/10 bg-white p-8 shadow-[0_18px_56px_rgba(11,42,74,0.06)]">
              <h2 className="text-2xl font-semibold text-[#0b2a4a]">{item.title}</h2>
              <p className="mt-4 text-base leading-7 text-[#0b2a4a]/72">{item.description}</p>
            </article>
          ))}
        </div>

        <div className="mt-16">
          <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-[#f08a24]">{t.featuresTitle}</h3>
          <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {t.features.map((feature) => {
              const Icon = feature.icon
              return (
                <article key={feature.title} className="rounded-[28px] border border-[#0b2a4a]/10 bg-white p-6 shadow-[0_18px_56px_rgba(11,42,74,0.05)]">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0b2a4a]/6 text-[#0b2a4a]">
                    <Icon size={24} />
                  </div>
                  <h4 className="mt-5 text-xl font-semibold text-[#0b2a4a]">{feature.title}</h4>
                  <p className="mt-3 text-sm leading-7 text-[#0b2a4a]/72">{feature.description}</p>
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
